import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  FiBell,
  FiChevronLeft,
} from "react-icons/fi";
import { BsPinAngleFill } from "react-icons/bs";
import {
  fetchActiveNotifications,
  getUserNotificationStatus,
  markAsRead,
  getUnreadCount,
} from "./services/userNotificationService";
import "./NotificationScreen.css";

const NOTIF_TYPES = [
  { value: "all", label: "Tất cả", icon: "" },
  { value: "new_feature", label: "Tính năng mới", icon: "🆕" },
  { value: "update", label: "Cập nhật", icon: "🔄" },
  { value: "maintenance", label: "Bảo trì", icon: "🛠️" },
  { value: "event", label: "Sự kiện", icon: "🎉" },
  { value: "announcement", label: "Thông báo", icon: "📢" },
];

const NOTIF_TYPE_ICONS = {
  new_feature: "🆕",
  update: "🔄",
  maintenance: "🛠️",
  event: "🎉",
  announcement: "📢",
};

const NOTIF_TYPE_LABELS = {
  new_feature: "Tính năng mới",
  update: "Cập nhật",
  maintenance: "Bảo trì",
  event: "Sự kiện",
  announcement: "Thông báo",
};

function formatDate(date) {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatTimeAgo(date) {
  if (!date) return "";
  const now = new Date();
  const d = date instanceof Date ? date : new Date(date);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "Vừa xong";
  if (diff < 3600) return `${Math.floor(diff / 60)} phút trước`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} giờ trước`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} ngày trước`;
  return d.toLocaleDateString("vi-VN");
}

const NotificationScreen = () => {
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [readIds, setReadIds] = useState([]);
  const [selectedNotif, setSelectedNotif] = useState(null);
  const [loading, setLoading] = useState(true);
  const [readFilter, setReadFilter] = useState("all"); // all | unread
  const [typeFilter, setTypeFilter] = useState("all"); // all | new_feature | update | ...
  const [mobileShowDetail, setMobileShowDetail] = useState(false);

  const userId = localStorage.getItem("userId");
  const readSet = new Set(readIds);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [notifs, status] = await Promise.all([
        fetchActiveNotifications(50),
        getUserNotificationStatus(userId),
      ]);
      setNotifications(notifs);
      const currentReadIds = status.readNotificationIds || [];
      setReadIds(currentReadIds);
      const currentReadSet = new Set(currentReadIds);

      // Auto-select from navigation state
      const stateId = location.state?.selectedId;
      let autoSelected = null;
      if (stateId) {
        const target = notifs.find((n) => n.id === stateId);
        if (target) {
          autoSelected = target;
          setSelectedNotif(target);
          setMobileShowDetail(true);
        }
      } else if (notifs.length > 0 && !selectedNotif) {
        autoSelected = notifs[0];
        setSelectedNotif(notifs[0]);
      }

      // Auto-mark as read when a notification is auto-selected
      if (autoSelected && !currentReadSet.has(autoSelected.id) && userId) {
        await markAsRead(userId, autoSelected.id);
        setReadIds((prev) => [...new Set([...prev, autoSelected.id])]);
      }
    } catch (err) {
      console.error("NotificationScreen loadData error:", err);
    } finally {
      setLoading(false);
    }
  }, [userId, location.state]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSelect = async (notif) => {
    setSelectedNotif(notif);
    setMobileShowDetail(true);

    // Use functional state to check latest readIds (avoid stale closure)
    setReadIds((prev) => {
      if (prev.includes(notif.id)) return prev;
      // Trigger async markAsRead outside setState
      markAsRead(userId, notif.id).catch((err) =>
        console.error("Error marking notification as read:", err)
      );
      return [...new Set([...prev, notif.id])];
    });
  };

  const handleMobileBack = () => {
    setMobileShowDetail(false);
  };

  const filteredNotifications = notifications.filter((n) => {
    if (readFilter === "unread" && readSet.has(n.id)) return false;
    if (typeFilter !== "all" && (n.type || "announcement") !== typeFilter) return false;
    return true;
  });

  const unreadCount = getUnreadCount(notifications, readIds);

  if (loading) {
    return (
      <div className="notif-screen-loading">
        <div className="notif-spinner" />
        <span>Đang tải thông báo...</span>
      </div>
    );
  }

  return (
    <div className="notif-screen">
      {/* Left Panel - List */}
      <div
        className={`notif-list-panel ${mobileShowDetail ? "notif-list-hidden-mobile" : ""}`}
      >
        <div className="notif-list-header">
          <div className="notif-list-title">
            <FiBell size={20} />
            <h2>Thông báo</h2>
            {unreadCount > 0 && (
              <span className="notif-unread-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>
            )}
          </div>
          <div className="notif-filter-group">
            <button
              className={`notif-filter-btn ${readFilter === "all" ? "active" : ""}`}
              onClick={() => setReadFilter("all")}
            >
              Tất cả
            </button>
            <button
              className={`notif-filter-btn ${readFilter === "unread" ? "active" : ""}`}
              onClick={() => setReadFilter("unread")}
            >
              Chưa đọc
            </button>
          </div>
          {/* Type filter pills */}
          <div className="notif-type-filters">
            {NOTIF_TYPES.map((t) => (
              <button
                key={t.value}
                className={`notif-type-pill ${typeFilter === t.value ? "active" : ""}`}
                onClick={() => setTypeFilter(t.value)}
              >
                {t.icon && <span>{t.icon}</span>}
                <span>{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="notif-list-content">
          {filteredNotifications.length === 0 ? (
            <div className="notif-empty">
              <div className="notif-empty-icon">📭</div>
              <p>
                {readFilter === "unread"
                  ? "Bạn đã đọc tất cả thông báo"
                  : typeFilter !== "all"
                    ? "Không có thông báo nào thuộc loại này"
                    : "Chưa có thông báo nào"}
              </p>
            </div>
          ) : (
            filteredNotifications.map((notif) => {
              const isRead = readSet.has(notif.id);
              const isSelected = selectedNotif?.id === notif.id;
              const typeIcon = NOTIF_TYPE_ICONS[notif.type] || NOTIF_TYPE_ICONS.announcement;
              return (
                <button
                  key={notif.id}
                  className={`notif-list-item ${isSelected ? "selected" : ""} ${!isRead ? "unread" : ""} ${notif.isPinned ? "pinned" : ""}`}
                  onClick={() => handleSelect(notif)}
                >
                  <div className="notif-item-type-icon">
                    {typeIcon}
                  </div>
                  <div className="notif-item-content">
                    <div className="notif-item-title-row">
                      {notif.isPinned && (
                        <BsPinAngleFill size={12} className="notif-item-pin" />
                      )}
                      <h4 className={`notif-item-title ${!isRead ? "font-medium" : "text-slate-600"}`}>
                        {notif.title}
                      </h4>
                    </div>
                    {notif.description && (
                      <p className="notif-item-desc">
                        {notif.description.replace(/<[^>]*>/g, "").substring(0, 100)}
                      </p>
                    )}
                    <span className="notif-item-time">
                      {formatTimeAgo(notif.createdAt)}
                    </span>
                  </div>
                  <div className="notif-item-indicator">
                    {!isRead && <div className="unread-dot"></div>}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Right Panel - Detail */}
      <div
        className={`notif-detail-panel ${mobileShowDetail ? "notif-detail-show-mobile" : ""}`}
      >
        {selectedNotif ? (
          <>
            <div className="notif-detail-header">
              <button
                className="notif-back-btn"
                onClick={handleMobileBack}
              >
                <FiChevronLeft size={20} />
                <span>Quay lại</span>
              </button>
              <span className="notif-detail-date">
                {formatDate(selectedNotif.createdAt)}
              </span>
            </div>

            <div className="notif-detail-content">
              <div className="notif-detail-top-meta">
                <span className={`notif-detail-type-badge type-${selectedNotif.type || "announcement"}`}>
                  {NOTIF_TYPE_ICONS[selectedNotif.type] || "📢"} {NOTIF_TYPE_LABELS[selectedNotif.type] || "Thông báo"}
                </span>
                {selectedNotif.isPinned && (
                  <span className="notif-detail-pin-badge">
                    <BsPinAngleFill size={12} /> Đã ghim
                  </span>
                )}
              </div>

              <h1 className="notif-detail-title">{selectedNotif.title}</h1>

              {selectedNotif.displayDate && (
                <div className="notif-detail-meta">
                  <span>📅 Ngày hiển thị: {formatDate(selectedNotif.displayDate)}</span>
                </div>
              )}
              {selectedNotif.expiryDate && (
                <div className="notif-detail-meta">
                  <span>⏰ Hết hạn: {formatDate(selectedNotif.expiryDate)}</span>
                </div>
              )}

              <div
                className="notif-detail-body"
                dangerouslySetInnerHTML={{
                  __html: selectedNotif.description || "<p>Không có nội dung</p>",
                }}
              />
            </div>
          </>
        ) : (
          <div className="notif-detail-empty">
            <div className="notif-detail-empty-icon">📩</div>
            <h3>Chọn một thông báo để xem chi tiết</h3>
            <p>Nhấp vào thông báo bên trái để đọc nội dung</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationScreen;
